import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';

interface NumeroAnimadoProps {
  /** Já formatado: "R$ 1,94", "34", "12 fichas". */
  valor: string;
  className?: string;
}

const DURACAO_MS = 200;

interface Troca {
  atual: string;
  /** Valor que está saindo (só existe durante os 200 ms da rolagem). */
  anterior: string | null;
}

// Quando o número muda, os dígitos rolam para cima em 200 ms.
// A fita que rola fica aria-hidden e é retirada do DOM assim que a animação
// termina: leitores de tela e cópia de texto pegam só o valor atual, uma vez.
export default function NumeroAnimado({ valor, className = '' }: NumeroAnimadoProps) {
  const reduzir = useReducedMotion();
  const primeiraCarga = useRef(true);
  const [{ atual, anterior }, setTroca] = useState<Troca>({ atual: valor, anterior: null });

  // Ajusta a troca durante o render (mesmo render que já traria o valor novo),
  // assim o número novo e o antigo entram em cena no mesmo quadro.
  if (atual !== valor) {
    setTroca({ atual: valor, anterior: reduzir ? null : atual });
  }

  useEffect(() => {
    primeiraCarga.current = false;
  }, []);

  // Terminada a rolagem, fica no DOM só o valor atual.
  useEffect(() => {
    if (anterior === null) return undefined;
    const t = window.setTimeout(() => {
      setTroca((estado) => (estado.anterior === anterior ? { ...estado, anterior: null } : estado));
    }, DURACAO_MS);
    return () => window.clearTimeout(t);
  }, [anterior]);

  const rolando = anterior !== null;

  return (
    <span className={`relative inline-flex overflow-hidden align-bottom ${className}`}>
      {rolando && <span className="sr-only">{atual}</span>}

      <span className="relative inline-block">
        {rolando && (
          <motion.span
            key={`anterior-${anterior}`}
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 whitespace-nowrap"
            initial={{ y: 0, opacity: 1 }}
            animate={{ y: '-70%', opacity: 0 }}
            transition={{ duration: DURACAO_MS / 1000, ease: 'easeOut' }}
          >
            {anterior}
          </motion.span>
        )}

        <motion.span
          key={`atual-${atual}`}
          aria-hidden={rolando ? 'true' : undefined}
          className="relative block whitespace-nowrap"
          initial={primeiraCarga.current ? false : { y: '70%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: DURACAO_MS / 1000, ease: 'easeOut' }}
        >
          {atual}
        </motion.span>
      </span>
    </span>
  );
}